// Angular
import { Component, viewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
// Material
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
// Constants
import { FREE_SEARCH_OPTIONS } from '@constants';
// Transloco
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
// Lodash
import { trim, toUpper } from 'lodash';
// Pipe
import { MakeMePrettyPipe } from '@app/core/_base/layout';
// Store
import { Store } from '@ngrx/store';
import { FtsAppState, saveFts } from '@app/fts-store';

@Component({
  selector: 'app-free-search',
  templateUrl: './free-search.component.html',
  styleUrls: ['./free-search.component.scss'],
  imports: [TranslocoDirective, FormsModule, MatFormField, MatLabel, MatInput, MatIconButton, MatSuffix, MatIcon, MatError],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [MakeMePrettyPipe],
})
export class FreeSearchComponent {
  // Private
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly makeMePretty = inject(MakeMePrettyPipe);
  private readonly store = inject<Store<FtsAppState>>(Store);

  // Public params
  searchOptionList: any[] = FREE_SEARCH_OPTIONS;
  searchOption: number = 8;
  inputText: string = '';
  loadingAfterSearch = signal<boolean>(false);

  // Get Template driven form access
  readonly freeSearchForm = viewChild<NgForm>('freeSearchForm');

  /**
   * On select search option
   * @param {number} value
   * @memberof FreeSearchComponent
   */
  onSelectSearchOption(value: number): void {
    this.searchOption = value;
  }

  /**
   * On Search by OEM, FTS, Gearbox code & Engine code
   * @returns {void}
   * @memberof FreeSearchComponent
   */
  onSearch(): void {
    if (this.freeSearchForm()?.invalid) return;

    this.loadingAfterSearch.set(true);

    const query = toUpper(trim(this.inputText));

    const link = {
      lang: this.translocoService.getActiveLang(),
      search: this.makeMePretty.transform(
        this.translocoService.translate('label_search'),
      ),
      type: this.makeMePretty.transform(
        this.translocoService.translate('label_type'),
      ),
    };

    switch (this.searchOption) {
      case 6: {
        // Engine Code
        const labelEngine = this.makeMePretty.transform(
          this.translocoService.translate('label_engine'),
        );
        this.router.navigate(
          [`/${link.lang}/pb/${link.search}/${link.type}/s6/${labelEngine}`],
          { queryParams: { query } },
        );
        break;
      }

      case 7: {
        // Gearbox Code
        const labelGearbox = this.makeMePretty.transform(
          this.translocoService.translate(
            'label_gear_box__drive_axle__middle_axle',
          ),
        );
        this.router.navigate(
          [`/${link.lang}/pb/${link.search}/${link.type}/s7/${labelGearbox}`],
          { queryParams: { query } },
        );
        break;
      }

      case 8: {
        // OEM
        this.router.navigate([`/${link.lang}/OEM/${query}`]);
        break;
      }

      case 11: {
        // Part Item
        this.router.navigate([`/${link.lang}/part-item/${query}`]);
        break;
      }

      case 15: {
        // FTS
        this.router.navigate(
          [`/${link.lang}/pb/${link.search}/${link.type}/s15/FTS`],
          { queryParams: { query } },
        );
        break;
      }
    }

    this.store.dispatch(
      saveFts({ searchOption: this.searchOption, searchText: query }),
    );
  }

  /**
   * On Clear FTS Input
   * @memberof FreeSearchComponent
   */
  onClearInput(): void {
    this.inputText = '';
    this.store.dispatch(
      saveFts({ searchOption: this.searchOption, searchText: '' }),
    );
  }
}
